import React, { useState } from "react";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Switch } from "@/shared/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/shared/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { Badge } from "@/shared/components/ui/badge";
import { Slider } from "@/shared/components/ui/slider";
import { Plus, X } from "lucide-react";
import { useLanguage } from "@/shared/components/shared/LanguageContext";

export default function ProviderForm({ provider, open, onClose, onSave }) {
  const { t } = useLanguage();
  const [formData, setFormData] = useState(
    provider || {
      name: "",
      code: "",
      type: "bank",
      status: "active",
      priority: 1,
      weight: 100,
      health_score: 100,
      success_rate: 100,
      avg_response_time: 0,
      supports_deposit: true,
      supports_withdrawal: true,
      supports_qr: false,
      min_amount: 1,
      max_amount: 1000000,
      daily_limit: 50000000,
      current_daily_volume: 0,
      api_endpoint: "",
      api_key: "",
      api_secret: "",
      webhook_secret: "",
      ip_whitelist: [],
      fee_percentage: 1.5,
      settlement_time: "instant",
      logo_url: "",
      notes: "",
    }
  );
  const [newIp, setNewIp] = useState("");
  const [saving, setSaving] = useState(false);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const addIp = () => {
    if (newIp && !formData.ip_whitelist?.includes(newIp)) {
      handleChange("ip_whitelist", [...(formData.ip_whitelist || []), newIp]);
      setNewIp("");
    }
  };

  const removeIp = (ip) => {
    handleChange("ip_whitelist", formData.ip_whitelist?.filter((i) => i !== ip));
  };

  const handleSubmit = async () => {
    setSaving(true);
    await onSave(formData);
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {provider ? t("editProvider") : t("addNewProvider")}
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="general" className="mt-4">
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="general">{t("general")}</TabsTrigger>
            <TabsTrigger value="api">{t("apiConfig")}</TabsTrigger>
            <TabsTrigger value="routing">{t("routingLimits")}</TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("providerNameRequired")}</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  placeholder={t("providerNamePlaceholder")}
                />
              </div>
              <div className="space-y-2">
                <Label>{t("providerCodeRequired")}</Label>
                <Input
                  value={formData.code}
                  onChange={(e) => handleChange("code", e.target.value.toUpperCase())}
                  placeholder={t("providerCodePlaceholder")}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("type")}</Label>
                <Select
                  value={formData.type}
                  onValueChange={(v) => handleChange("type", v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank">{t("bank")}</SelectItem>
                    <SelectItem value="ewallet">{t("providerTypeEwallet")}</SelectItem>
                    <SelectItem value="qr">{t("providerTypeQr")}</SelectItem>
                    <SelectItem value="card">{t("providerTypeCard")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("status")}</Label>
                <Select
                  value={formData.status}
                  onValueChange={(v) => handleChange("status", v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">{t("active")}</SelectItem>
                    <SelectItem value="inactive">{t("inactive")}</SelectItem>
                    <SelectItem value="maintenance">{t("maintenance")}</SelectItem>
                    <SelectItem value="degraded">{t("degraded")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <Label>{t("supportsDeposits")}</Label>
                  <p className="text-sm text-slate-500">{t("processDepositTransactions")}</p>
                </div>
                <Switch
                  checked={formData.supports_deposit}
                  onCheckedChange={(v) => handleChange("supports_deposit", v)}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>{t("supportsWithdrawals")}</Label>
                  <p className="text-sm text-slate-500">{t("processWithdrawalTransactions")}</p>
                </div>
                <Switch
                  checked={formData.supports_withdrawal}
                  onCheckedChange={(v) => handleChange("supports_withdrawal", v)}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>{t("supportsQr")}</Label>
                  <p className="text-sm text-slate-500">{t("generatePromptPayQr")}</p>
                </div>
                <Switch
                  checked={formData.supports_qr}
                  onCheckedChange={(v) => handleChange("supports_qr", v)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t("logoUrl")}</Label>
              <Input
                value={formData.logo_url}
                onChange={(e) => handleChange("logo_url", e.target.value)}
                placeholder={t("logoUrlPlaceholder")}
              />
            </div>

            <div className="space-y-2">
              <Label>{t("notes")}</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => handleChange("notes", e.target.value)}
                rows={3}
              />
            </div>
          </TabsContent>

          <TabsContent value="api" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>{t("apiEndpoint")}</Label>
              <Input
                value={formData.api_endpoint}
                onChange={(e) => handleChange("api_endpoint", e.target.value)}
                placeholder={t("apiEndpointPlaceholder")}
              />
            </div>

            <div className="space-y-2">
              <Label>{t("apiKey")}</Label>
              <Input
                value={formData.api_key}
                onChange={(e) => handleChange("api_key", e.target.value)}
                type="password"
              />
            </div>

            <div className="space-y-2">
              <Label>{t("apiSecret")}</Label>
              <Input
                value={formData.api_secret}
                onChange={(e) => handleChange("api_secret", e.target.value)}
                type="password"
              />
            </div>

            <div className="space-y-2">
              <Label>{t("webhookSecret")}</Label>
              <Input
                value={formData.webhook_secret}
                onChange={(e) => handleChange("webhook_secret", e.target.value)}
                type="password"
              />
            </div>

            <div className="space-y-2">
              <Label>{t("providerIpWhitelist")}</Label>
              <div className="flex gap-2">
                <Input
                  value={newIp}
                  onChange={(e) => setNewIp(e.target.value)}
                  placeholder={t("ipWhitelistPlaceholder")}
                />
                <Button onClick={addIp}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.ip_whitelist?.map((ip) => (
                  <Badge key={ip} variant="secondary" className="gap-1">
                    {ip}
                    <button onClick={() => removeIp(ip)}>
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="routing" className="space-y-4 mt-4">
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <Label>{t("priorityHighest")}</Label>
                  <span className="text-sm text-slate-500">{formData.priority}</span>
                </div>
                <Slider
                  value={[formData.priority]}
                  onValueChange={([v]) => handleChange("priority", v)}
                  min={1}
                  max={10}
                  step={1}
                />
              </div>

              <div className="space-y-2">
                <div className="flex justify-between">
                  <Label>{t("weightLoadBalancing")}</Label>
                  <span className="text-sm text-slate-500">{formData.weight}%</span>
                </div>
                <Slider
                  value={[formData.weight]}
                  onValueChange={([v]) => handleChange("weight", v)}
                  min={0}
                  max={100}
                  step={5}
                />
              </div>

              <div className="space-y-2">
                <div className="flex justify-between">
                  <Label>{t("healthScore")}</Label>
                  <span className="text-sm text-slate-500">{formData.health_score}%</span>
                </div>
                <Slider
                  value={[formData.health_score]}
                  onValueChange={([v]) => handleChange("health_score", v)}
                  min={0}
                  max={100}
                  step={1}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("minAmountThb")}</Label>
                <Input
                  type="number"
                  value={formData.min_amount}
                  onChange={(e) => handleChange("min_amount", parseInt(e.target.value))}
                />
              </div>
              <div className="space-y-2">
                <Label>{t("maxAmountThb")}</Label>
                <Input
                  type="number"
                  value={formData.max_amount}
                  onChange={(e) => handleChange("max_amount", parseInt(e.target.value))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t("dailyLimitThb")}</Label>
              <Input
                type="number"
                value={formData.daily_limit}
                onChange={(e) => handleChange("daily_limit", parseInt(e.target.value))}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("feePercentage")} (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.fee_percentage}
                  onChange={(e) => handleChange("fee_percentage", parseFloat(e.target.value))}
                />
              </div>
              <div className="space-y-2">
                <Label>{t("settlementTimeLabel")}</Label>
                <Select
                  value={formData.settlement_time}
                  onValueChange={(v) => handleChange("settlement_time", v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="instant">{t("instant")}</SelectItem>
                    <SelectItem value="1hour">{t("oneHour")}</SelectItem>
                    <SelectItem value="same_day">{t("sameDay")}</SelectItem>
                    <SelectItem value="next_day">{t("nextDay")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose}>
            {t("cancel")}
          </Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? t("saving") : provider ? t("updateProvider") : t("addProviderBtn")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}